import { z } from 'zod';

import {
  PurchaseAdjustmentKindSchema,
  PurchaseAdjustmentValueTypeSchema,
} from './purchases.schema.js';
import type {
  CreatePurchaseAdjustmentPresetRequest,
  CreatePurchaseAdjustmentPresetResponse,
  ListEffectivePurchaseAdjustmentPresetsRequest,
  ListEffectivePurchaseAdjustmentPresetsResponse,
  ListPurchaseAdjustmentPresetsRequest,
  ListPurchaseAdjustmentPresetsResponse,
  PurchaseAdjustmentPresetDto,
  PurchaseAdjustmentPresetEffectiveDto,
  SetPurchaseAdjustmentPresetEnabledRequest,
  SetPurchaseAdjustmentPresetEnabledResponse,
  SetPurchaseAdjustmentPresetMaterialsRequest,
  SetPurchaseAdjustmentPresetMaterialsResponse,
  SetPurchaseAdjustmentPresetRolesRequest,
  SetPurchaseAdjustmentPresetRolesResponse,
  SetPurchaseAdjustmentPresetUsersRequest,
  SetPurchaseAdjustmentPresetUsersResponse,
  UpdatePurchaseAdjustmentPresetRequest,
  UpdatePurchaseAdjustmentPresetResponse,
} from './purchases-adjustment-presets.dto.js';

const presetCodeSchema = z.string().trim().min(1).max(64);
const presetLabelSchema = z.string().trim().min(1).max(120);
const reasonCodeSchema = z.string().trim().min(1).max(100);
const reasonNoteSchema = z.string().trim().min(1).max(500);

function deriveValueType(
  kind: 'PROMOTION_BONUS' | 'QUALITY_DISCOUNT',
): 'centsPerLb' | 'percent' {
  return kind === 'PROMOTION_BONUS' ? 'centsPerLb' : 'percent';
}

const PurchaseAdjustmentPresetSchemaBase = z.object({
  id: z.uuid(),
  companyId: z.uuid(),
  code: presetCodeSchema,
  label: presetLabelSchema,
  kind: PurchaseAdjustmentKindSchema,
  valueType: PurchaseAdjustmentValueTypeSchema,
  value: z.number().nonnegative(),
  reasonCode: reasonCodeSchema.nullable(),
  reasonNoteDefault: reasonNoteSchema.nullable(),
  enabled: z.boolean(),
  materialIds: z.array(z.uuid()),
  roleIds: z.array(z.uuid()),
  userIds: z.array(z.uuid()),
  createdByUserId: z.uuid().nullable(),
  updatedByUserId: z.uuid().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const PurchaseAdjustmentPresetSchema: z.ZodType<PurchaseAdjustmentPresetDto> =
  PurchaseAdjustmentPresetSchemaBase.refine(
    (value) =>
      value.valueType === deriveValueType(value.kind) &&
      (value.kind === 'QUALITY_DISCOUNT' ? Boolean(value.reasonCode) : true),
    {
      message: 'Preset kind/valueType or reasonCode is invalid.',
      path: ['valueType'],
    },
  );

export const PurchaseAdjustmentPresetEffectiveSchema: z.ZodType<PurchaseAdjustmentPresetEffectiveDto> =
  z
    .object({
      id: z.uuid(),
      code: presetCodeSchema,
      label: presetLabelSchema,
      kind: PurchaseAdjustmentKindSchema,
      valueType: PurchaseAdjustmentValueTypeSchema,
      value: z.number().nonnegative(),
      reasonCode: reasonCodeSchema.nullable(),
      reasonNoteDefault: reasonNoteSchema.nullable(),
      materialIds: z.array(z.uuid()),
    })
    .refine(
      (value) =>
        value.valueType === deriveValueType(value.kind) &&
        (value.kind === 'QUALITY_DISCOUNT' ? Boolean(value.reasonCode) : true),
      {
        message: 'Preset kind/valueType or reasonCode is invalid.',
        path: ['valueType'],
      },
    );

export const ListPurchaseAdjustmentPresetsRequestSchema: z.ZodType<ListPurchaseAdjustmentPresetsRequest> =
  z.object({
    companyId: z.uuid(),
  });

export const ListPurchaseAdjustmentPresetsResponseSchema: z.ZodType<ListPurchaseAdjustmentPresetsResponse> =
  z.object({
    items: z.array(PurchaseAdjustmentPresetSchema),
  });

export const CreatePurchaseAdjustmentPresetRequestSchema: z.ZodType<CreatePurchaseAdjustmentPresetRequest> =
  z
    .object({
      companyId: z.uuid(),
      code: presetCodeSchema,
      label: presetLabelSchema,
      kind: PurchaseAdjustmentKindSchema,
      value: z.number().nonnegative(),
      reasonCode: reasonCodeSchema.nullable().optional(),
      reasonNoteDefault: reasonNoteSchema.nullable().optional(),
      enabled: z.boolean().optional(),
    })
    .refine((value) => (value.kind === 'QUALITY_DISCOUNT' ? Boolean(value.reasonCode) : true), {
      message: 'reasonCode is required for quality discount presets.',
      path: ['reasonCode'],
    });

export const CreatePurchaseAdjustmentPresetResponseSchema: z.ZodType<CreatePurchaseAdjustmentPresetResponse> =
  z.object({
    preset: PurchaseAdjustmentPresetSchema,
  });

export const UpdatePurchaseAdjustmentPresetRequestSchema: z.ZodType<UpdatePurchaseAdjustmentPresetRequest> =
  z.object({
    companyId: z.uuid(),
    presetId: z.uuid(),
    code: presetCodeSchema,
    label: presetLabelSchema,
    value: z.number().nonnegative(),
    reasonCode: reasonCodeSchema.nullable().optional(),
    reasonNoteDefault: reasonNoteSchema.nullable().optional(),
  });

export const UpdatePurchaseAdjustmentPresetResponseSchema: z.ZodType<UpdatePurchaseAdjustmentPresetResponse> =
  z.object({
    preset: PurchaseAdjustmentPresetSchema,
  });

export const SetPurchaseAdjustmentPresetEnabledRequestSchema: z.ZodType<SetPurchaseAdjustmentPresetEnabledRequest> =
  z.object({
    companyId: z.uuid(),
    presetId: z.uuid(),
    enabled: z.boolean(),
  });

export const SetPurchaseAdjustmentPresetEnabledResponseSchema: z.ZodType<SetPurchaseAdjustmentPresetEnabledResponse> =
  z.object({
    preset: PurchaseAdjustmentPresetSchema,
  });

export const SetPurchaseAdjustmentPresetMaterialsRequestSchema: z.ZodType<SetPurchaseAdjustmentPresetMaterialsRequest> =
  z.object({
    companyId: z.uuid(),
    presetId: z.uuid(),
    materialIds: z.array(z.uuid()),
  });

export const SetPurchaseAdjustmentPresetMaterialsResponseSchema: z.ZodType<SetPurchaseAdjustmentPresetMaterialsResponse> =
  z.object({
    preset: PurchaseAdjustmentPresetSchema,
  });

export const SetPurchaseAdjustmentPresetRolesRequestSchema: z.ZodType<SetPurchaseAdjustmentPresetRolesRequest> =
  z.object({
    companyId: z.uuid(),
    presetId: z.uuid(),
    roleIds: z.array(z.uuid()),
  });

export const SetPurchaseAdjustmentPresetRolesResponseSchema: z.ZodType<SetPurchaseAdjustmentPresetRolesResponse> =
  z.object({
    preset: PurchaseAdjustmentPresetSchema,
  });

export const SetPurchaseAdjustmentPresetUsersRequestSchema: z.ZodType<SetPurchaseAdjustmentPresetUsersRequest> =
  z.object({
    companyId: z.uuid(),
    presetId: z.uuid(),
    userIds: z.array(z.uuid()),
  });

export const SetPurchaseAdjustmentPresetUsersResponseSchema: z.ZodType<SetPurchaseAdjustmentPresetUsersResponse> =
  z.object({
    preset: PurchaseAdjustmentPresetSchema,
  });

export const ListEffectivePurchaseAdjustmentPresetsRequestSchema: z.ZodType<ListEffectivePurchaseAdjustmentPresetsRequest> =
  z.object({
    companyId: z.uuid(),
  });

export const ListEffectivePurchaseAdjustmentPresetsResponseSchema: z.ZodType<ListEffectivePurchaseAdjustmentPresetsResponse> =
  z.object({
    items: z.array(PurchaseAdjustmentPresetEffectiveSchema),
  });
